// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/commentSourceUtils.test.ts
/**
 * Shared citation helpers — the sanctioned home reachable from `src/ask-v2`,
 * `src/components` and `src/routes` alike.
 *
 * WHAT MOVED (this module is new): the WHOLE union-dispatch layer was
 * EXTRACTED out of `routes/ask-puntt/utils/commentSourceUtils.ts` — every
 * variant predicate (`isKnowledgeRuleCitation`, `isFileCitation`,
 * `isNavigableFileCitation`, `isTextCitation`, `isAgentSourceCitation`,
 * `isKnowledgeDbCitation`, `isGraphCitation`), the dispatcher itself
 * (`CitationVariant` + `resolveCitationVariant`), the drawer scope list
 * (`DRAWER_RENDERABLE_VARIANTS` + `isDrawerRenderableVariant`),
 * `cellAnchorLabel` and the `TicketMessageCitation` type. This file — not any
 * one surface's branch — is therefore what a revert of the shared-dispatch
 * behavior has to target.
 *
 * WHAT DELIBERATELY STAYED in `commentSourceUtils.ts`: labelling
 * (`citationLabel`, `reviewCommentSourceSummaryLabel`), de-duplication
 * (`dedupeRuleCitations`, `dedupeGraphCitations`) and the context-pane target /
 * pin types. `citationLabel` needs `formatTimeStamp` from `routes/tickets`, so
 * the dependency cannot point the other way.
 *
 * WHO CONSUMES IT, and why the blast radius is worth naming — `cellAnchorLabel`
 * renders user-visible text on all three:
 *   - `routes/tickets/.../comment-drawer/CommentCitationSource.tsx`
 *     (`resolveCitationVariant`, `isDrawerRenderableVariant`,
 *     `isKnowledgeRuleCitation`)
 *   - `components/KnowledgeRuleCitationDialog.tsx` — itself RELOCATED in this
 *     change out of `routes/ask-puntt/components/`, so the tickets drawer can
 *     render a rule citation without importing one route tree's private module
 *     (see that file's own "WHY IT LIVES IN `src/components/`" note)
 *   - `routes/ask-puntt/utils/commentSourceUtils.ts`
 *
 * THE RE-EXPORT IS DELIBERATE, NOT LEFTOVER: `commentSourceUtils.ts` re-exports
 * every EXPORTED name above (`DRAWER_RENDERABLE_VARIANTS` stays private to this
 * file) so the existing `routes/ask-puntt` and test imports keep resolving
 * without a repo-wide import rewrite. Import from HERE in new code;
 * the shim stays until those call sites are migrated.
 */
import {
  type CitationCellAnchor,
  type FileCitation,
  type TextCitation,
  ticketMessageCitationSchema,
} from "@mg/schemas/src/commons/ticket";
import { type z } from "zod";

export type TicketMessageCitation = z.infer<typeof ticketMessageCitationSchema>;

/** A synthesized Knowledge Database rule (`knowledgerules`) cited on a review
 * comment. `ruleId` is unique to this variant across the citation union, so it
 * is a safe discriminant. The citation carries its own `quote` so the chip is
 * self-describing without a follow-up fetch. */
export function isKnowledgeRuleCitation(
  citation: TicketMessageCitation,
): citation is Extract<TicketMessageCitation, { ruleId: string }> {
  return "ruleId" in citation;
}

export function isFileCitation(
  citation: TicketMessageCitation,
): citation is FileCitation {
  return (
    "url" in citation ||
    "originalURL" in citation ||
    "rel_x" in citation ||
    "videoStart" in citation ||
    "cellAnchor" in citation
  );
}

export function isNavigableFileCitation(
  citation: TicketMessageCitation,
): citation is FileCitation {
  return (
    isFileCitation(citation) &&
    ("originalURL" in citation ||
      "rel_x" in citation ||
      "videoStart" in citation ||
      "cellAnchor" in citation ||
      Boolean(citation.url))
  );
}

/** The DISCRIMINANTS of every more specific citation variant.
 *
 * why this list exists: `isTextCitation` tests for a `text` KEY, not a usable
 * value, so any richer citation that happens to carry `text` was captured by
 * it -- and because the dispatcher reached it first, the citation rendered as
 * a bare guideline excerpt and lost its link to the source. A rule citation
 * swallowed this way shows no rule, no source document, and no page pin; when
 * its `text` is also empty the dialog falls through `urlToPreviewType` to
 * "Sorry, we can't preview this file type."
 *
 * Kept beside `isTextCitation` rather than inlined, because the failure is
 * silent: nothing errors, the chip simply renders as the wrong thing. A new
 * variant added to the union without a line here re-opens it. */
const SPECIFIC_CITATION_KEYS = [
  "ruleId",
  "graphNodeId",
  "knowledgeDataRecordId",
  "sourceId",
] as const;

export function isTextCitation(
  citation: TicketMessageCitation,
): citation is TextCitation {
  if (!("text" in citation) || "url" in citation) return false;
  // Never claim a citation that a more specific branch can render properly.
  return !SPECIFIC_CITATION_KEYS.some((key) => key in citation);
}

/** An agent naming its own source: a ticket comment, a document page, or a
 * chat message, cited by id rather than by URL.
 *
 * why it needs a predicate at all: the union has carried this variant since
 * warhol began answering, and nothing in carrie ever tested for it -- so every
 * agent-sourced citation fell past all four branches of `CommentCitationLinks`
 * onto the inert "Source" + warning fallback. The chip named nothing and went
 * nowhere.
 *
 * `source` + `sourceId` together are the discriminant: `sourceId` alone also
 * appears on a rule citation, so this is tested AFTER `isKnowledgeRuleCitation`
 * and requires the `source` label a rule citation never carries. */
export function isAgentSourceCitation(
  citation: TicketMessageCitation,
): citation is Extract<TicketMessageCitation, { source: string }> {
  return (
    "source" in citation && "sourceId" in citation && !("ruleId" in citation)
  );
}

export function isKnowledgeDbCitation(
  citation: TicketMessageCitation,
): citation is { knowledgeDataRecordId: string } {
  return "knowledgeDataRecordId" in citation;
}

/** A relationship the knowledge GRAPH found: which rules connect, and how.
 *
 * `graphNodeId` is unique to this variant across the citation union, so it is a
 * safe discriminant. Distinct from a rule citation on purpose — a rule chip
 * answers "which rule backs this", and this answers "these rules relate, and
 * here is the conflict / requirement / conversation that made them worth
 * surfacing". Flattened into rule chips that reason is lost.
 *
 * Node ids are `kind:native`. `claim:`, `chat:`, `user:` and `memory:` carry the
 * real row id and are safe to link. `brand:` and `requirement:` are DERIVED from
 * text — render them, never store them as keys. */
export function isGraphCitation(
  citation: TicketMessageCitation,
): citation is Extract<TicketMessageCitation, { graphNodeId: string }> {
  return "graphNodeId" in citation;
}

/** Which arm of the citation union a surface is being asked to render.
 *
 * `"unrenderable"` is a real answer, not an error: a citation that names no
 * file, no rule, no graph node, no KDB record, no agent source and no excerpt
 * has nothing any of these surfaces can show.
 */
export type CitationVariant =
  | "file"
  | "rule"
  | "graph"
  | "knowledge-record"
  | "agent-source"
  | "text"
  | "unrenderable";

/**
 * The ONE dispatcher over the citation union.
 *
 * why it exists: the Ask Puntt comment panel (`CommentCitationLinks`) and the
 * tickets comment drawer (`comment-drawer/Comment.tsx`) each used to walk their
 * own if-chain over the same union, and they disagreed. The drawer tested only
 * `isKnowledgeRuleCitation` and let everything else fall to its generic dialog,
 * whose `Preview` then got `url` / `text` / `knowledgeDataRecord` all undefined
 * — a "Source" link that opens "Sorry, we can't preview this file type." Both
 * surfaces now ask this function, so a variant added to the union is handled,
 * or explicitly declared unrenderable, in one place.
 *
 * ORDER IS THE CONTRACT, not style. Specific discriminants come first and the
 * generic `text` arm comes last: `isTextCitation` answers on the presence of a
 * `text` KEY, so testing it earlier lets any richer citation that happens to
 * carry one render as a bare guideline excerpt, losing its rule, its source
 * document and its page pin. (`isTextCitation` also refuses those keys itself,
 * so this is belt and braces; both are cheap and the failure is silent.)
 * `isAgentSourceCitation` must stay after the rule arm — a rule citation also
 * carries `sourceId`.
 */
export function resolveCitationVariant(
  citation: TicketMessageCitation,
): CitationVariant {
  if (isNavigableFileCitation(citation)) return "file";
  if (isKnowledgeRuleCitation(citation)) return "rule";
  if (isGraphCitation(citation)) return "graph";
  if (isKnowledgeDbCitation(citation)) return "knowledge-record";
  if (isAgentSourceCitation(citation)) return "agent-source";
  if (isTextCitation(citation)) return "text";
  return "unrenderable";
}

/** The variants the tickets comment drawer has a renderer for.
 *
 * The drawer is deliberately NARROWER than the Ask Puntt comment panel, and
 * this list is where that scope choice is written down instead of being a
 * silent fall-through. A graph subgraph and an agent-source quote each have a
 * dialog, but both live under `routes/ask-puntt` and the drawer cannot pull
 * them in: `GraphCitationDialog` imports `askPunttChatRoute`, i.e. the whole
 * Ask Puntt chat route tree. So the drawer shows no source affordance for
 * those arms rather than a "Source" link onto an empty dialog.
 *
 * A variant added to the union is unrenderable here until someone adds it to
 * this list AND gives `renderCitation` an arm for it — which is the point: the
 * default is "declared unrenderable", not "silently empty".
 */
const DRAWER_RENDERABLE_VARIANTS: readonly CitationVariant[] = [
  "file",
  "rule",
  "knowledge-record",
  "text",
];

/** True when the tickets comment drawer has a renderer for this variant. */
export function isDrawerRenderableVariant(variant: CitationVariant): boolean {
  return DRAWER_RENDERABLE_VARIANTS.includes(variant);
}

/**
 * Human-readable label for a citation into a spreadsheet source, e.g.
 * "Claims B7", "Claims row 42", or "Sheet 2".
 *
 * why: this is the sanctioned home for spreadsheet anchor labelling — the
 * tickets comment drawer, `KnowledgeRuleCitationDialog` and the Ask Puntt
 * comment utils all call THIS function, so they cannot drift apart on the
 * sheet-name fallback. One byte-identical twin still exists,
 * `citationCellAnchorLabel` in `routes/ask-puntt/components/AssetViewerPane.tsx`
 * — it predates this module and has zero call sites repo-wide, so it is
 * pre-existing dead code rather than a live second implementation. Do not add
 * callers to it; delete it in a PR that already touches that file.
 */
export function cellAnchorLabel(anchor: CitationCellAnchor): string {
  const sheet =
    anchor.sheetName?.trim() ||
    (anchor.sheetIndex != null ? `Sheet ${anchor.sheetIndex + 1}` : "Sheet");
  if (anchor.range) return `${sheet} ${anchor.range}`;
  if (anchor.row != null) return `${sheet} row ${anchor.row}`;
  return sheet;
}
